import React, { useEffect, useState } from 'react';
import { BrowserRouter as Router, Route } from 'react-router-dom';
import { Provider } from 'react-redux';
import { saveUserData, clearUserData, endUserSession, message_broadcast, message_receive} from '../services/API';
import { userTypes} from '../constants/constants';
import App from '../components/App';
import AmiraSignIn, {AmiraAuthenticator} from '../components/Login/AmiraSignIn';
import config from '../config';
import GlobalNav from '../components/Global/GlobalNav';
import cx from 'classnames';
import { Auth } from 'aws-amplify';
import {initAudio} from '../components/audiocapture/recorder';
import { addOrientationListener, getOrientation } from '../components/Orientation/orientationListener'
import Orientation from '../components/Orientation/Orientation';
import Loading from '../components/Global/Loading';
import { GetBrandedCopyright, GetBrandedFooter } from '../services/BrandingService';
import { redirectToLoginOrigin, saveLoginOrigin, wouldRedirectToLoginOrigin } from '../services/loginService.js';
import { logSentryException } from '../services/util';
import * as Sentry from '@sentry/browser';
import DebugMenu from '../components/DebugMenu/DebugMenu';

//polyfills
require('../polyfills.js');

// parse the search params from our current URL
// this is used to (a) deeplink to signup and (b) prepopulate verification
let urlParams = new URLSearchParams(window.location.search);
// Spanish pilot: URL only (?isSpanishPilot=true). Not persisted — inbox/launch must pass the param when needed.
// Evaluated once at module load; client navigations that drop the query string will not retain pilot mode.
const isSpanishPilot = urlParams.get('isSpanishPilot') === 'true';

// Debug menu only: forceBrand stored in sessionStorage at module load so it's available
// before any components mount (WebGLContext needs branding for character selection).
// Matches shouldShowDebugMenu() logic in DebugMenu.js
const stack = process.env.FEATURE_FLAGS_STACK;
const showDebug = urlParams.get('showDebug');
const isDebugMenuVisible = stack === 'DEV' || stack === 'STAGE' || showDebug === '1' || showDebug === 'true';
const forceBrand = urlParams.get('forceBrand');
if (isDebugMenuVisible && forceBrand) {
  sessionStorage.setItem('forceBrand', forceBrand);
} else if (!forceBrand) {
  sessionStorage.removeItem('forceBrand');
}

const AUTH_MODES = {signUp: 'signUp', signup: 'signUp', confirm: 'confirmSignUp', signin: 'signIn'};
const SIGNEDIN_MODE = {signedIn: 'signedIn'};
let token = null;
const sts = new AWS.STS();
let sso = false;

const isDevelopment = window.location.host.includes('localhost')
const MAX_RETRIES = 3;

// GT-102: the launch token lives 60 minutes, so an STS refresh that reuses it can never succeed.
// Credentials are minted once for the role's maximum lifetime instead, and the session ends on the
// session-ended page when they expire. Do not reintroduce a refresh without a fresh token.
const STS_DURATION_SECONDS = 28800; // 8 hours; the role allows up to 43200 (MaxSessionDuration of amira-WebApp-Auth-Cognito)
const STS_DURATION_MS = STS_DURATION_SECONDS * 1000;
// End the session this long before the credentials expire, so no request is ever signed with dead ones.
const SESSION_END_MARGIN_MS = 5 * 60 * 1000;
const SESSION_LENGTH_MS = STS_DURATION_MS - SESSION_END_MARGIN_MS;
// Wall-clock poll for the deadline. A single long setTimeout drifts by however long the device
// slept, so poll instead; the margin above is wider than this interval, so a check is never late.
const SESSION_CHECK_INTERVAL_MS = 60 * 1000;

export class AppContainer extends React.Component {
  constructor(props){
    super(props);

    this.state = {
      loading: true,
      userData: null,
      orientation: getOrientation(),
      preChecksDone: false,
    }

    this._sessionCheckId = null;
    this._sessionEndsAt = null;
    this._sessionEnded = false;
    this._sessionEndReason = null;
    this.orientationHandler = this.orientationHandler.bind(this)
    this.setPreChecksDone = this.setPreChecksDone.bind(this);
    this.assumeRole = this.assumeRole.bind(this);
    this.handleVisibilityChange = this.handleVisibilityChange.bind(this);
    this.handlePageShow = this.handlePageShow.bind(this);
  }

  orientationHandler(){
    this.setState({
      orientation: getOrientation()
    })
  }

  componentDidMount(){
    //TODO: Potentially resurrect this for development: this.props.authData && this.cacheData(this.props.authData);
    addOrientationListener(this.orientationHandler)

    // Check the session deadline whenever the tab comes back. A foreground device waking from sleep
    // does not always fire visibilitychange, so focus and pageshow (bfcache restore) are covered too.
    document.addEventListener('visibilitychange', this.handleVisibilityChange);
    window.addEventListener('focus', this.handleVisibilityChange);
    window.addEventListener('pageshow', this.handlePageShow);

    // Comment out next line to disable auto-logout when refreshing browser
    clearUserData()
    redirectToLoginOrigin(urlParams)

    // Saved before assumeRole so endExpiredSession can read it on the failure path.
    saveLoginOrigin(urlParams, urlParams.get('inboxReturnUrl'));

    if(urlParams.get("token")){
      token = urlParams.get("token");
      sso = true;
      this.assumeRole();
    }

    if( !this.state.userData ){
      this.props.webglProps.hideAvatar()
    }
  }

  getUserDataFromURL(){
    let grade = urlParams.get("grade");
    if(grade) grade = parseInt(grade);

    let schoolId = parseInt(urlParams.get("schoolId"));
    schoolId = !isNaN(schoolId) ? schoolId : null;

    let districtId = parseInt(urlParams.get("districtId"));
    districtId = !isNaN(districtId) ? districtId : null;

    let subdistrictId = (urlParams.get("subdistrictId"));
    subdistrictId = !isNaN(subdistrictId) ? subdistrictId : null;

    // Parse onCampus parameter - true if "true" or "1", false otherwise
    let onCampusParam = urlParams.get("onCampus");
    let onCampus = onCampusParam === "true" || onCampusParam === "1";

    const user = {
      attributes: {
        'custom:firstName': urlParams.get("firstName") || "DefaultFirst",
        'custom:lastName': urlParams.get("lastName") || "DefaultLast",
        'custom:sisId': urlParams.get("userId") || "12345678-1234-1234-1234-123456789012",
        'custom:userType': urlParams.get("role") ? userTypes[urlParams.get("role").toUpperCase()] : userTypes.STUDENT,
        grade: grade || 0,
        districtId: districtId || 12591,
        schoolId: schoolId || 12595,
        subdistrictId,
        correlationid: urlParams.get("correlationid"),
        skipMagic: true,
        onCampus
      }
    };
    return user;
  }

  //Cache the User Data
  componentDidUpdate(prevProps, prevState){
    if(this.props.authData != prevProps.authData){
      if(this.props.authData && (prevProps.authData === undefined)){
        if (isDevelopment)  this.cacheData(this.props.authData);
        else {
          console.log("Trying to auth with cached creds. Signing out.");
          clearUserData();
        }
      }else if(this.props.authData){
        this.cacheData(this.props.authData);
      }
    }

    if( this.state.userData && this.state.userData.userType === userTypes.STUDENT ){
      this.props.webglProps.showAvatar()
    }
    if( !prevState.userData && this.state.userData ){
      this.setState({
        loading: false
      })
    }
  }

  componentWillUnmount(){
    window.removeEventListener('resize', this.orientationHandler)
    document.removeEventListener('visibilitychange', this.handleVisibilityChange);
    window.removeEventListener('focus', this.handleVisibilityChange);
    window.removeEventListener('pageshow', this.handlePageShow);
    this.stopSessionCheck();
  }

  handleVisibilityChange() {
    // A suspended tab can outlive its STS session, so check the moment it comes back rather than
    // waiting for the next poll.
    if (!document.hidden) this.checkSessionExpiry();
  }

  handlePageShow(event) {
    // Back from the session-ended page can restore this page from the back/forward cache with the
    // old instance intact, including the "already ended" guard. An ended session stays ended
    // whatever the reason (a failed launch has no deadline to re-check), so end it again directly.
    if (event && event.persisted && this._sessionEnded) {
      this._sessionEnded = false;
      this.endExpiredSession(this._sessionEndReason);
      return;
    }
    this.handleVisibilityChange();
  }

  startSessionCheck() {
    this.stopSessionCheck();
    this._sessionEndsAt = Date.now() + SESSION_LENGTH_MS;
    this._sessionCheckId = setInterval(() => this.checkSessionExpiry(), SESSION_CHECK_INTERVAL_MS);
  }

  stopSessionCheck() {
    if (this._sessionCheckId) {
      clearInterval(this._sessionCheckId);
      this._sessionCheckId = null;
    }
  }

  checkSessionExpiry() {
    if (this._sessionEndsAt && Date.now() >= this._sessionEndsAt) {
      this.endExpiredSession('expired');
    }
  }

  // Ends the session visibly. endUserSession() is not used here: it depends on window.opener or a
  // trusted login_origin and is a silent no-op for many SSO launches, which left students running
  // on dead credentials for hours (GT-102). Idempotent: the poll and the visibility handler can
  // both fire on wake, and redirectToLoginOrigin clears loginOrigin before it navigates, so a
  // second call would go to /sessionEnded.html instead of the launcher.
  endExpiredSession(reason) {
    if (this._sessionEnded) return;
    this._sessionEnded = true;
    this._sessionEndReason = reason;
    this.stopSessionCheck();
    console.warn(`Ending session: ${reason}`);
    // Low-volume event so production shows how often each exit is taken.
    Sentry.captureMessage('Session ended', { level: 'info', tags: { sessionEndReason: reason } });
    // Always navigates: the launcher's login page when it is known and trusted, otherwise the
    // session-ended page. Never reload: the launch token in the URL is long dead and would strand
    // the student on the Loading screen. Flush first so queued Sentry events leave before the
    // navigation unloads the page.
    const hasTrustedOrigin = wouldRedirectToLoginOrigin(null, false);
    return Sentry.flush(2000).finally(() => redirectToLoginOrigin(null, !hasTrustedOrigin));
  }

  federatedLogIn(sessionToken){
    const expires_at = STS_DURATION_MS + Date.now();
    return this.performFederatedSignIn(sessionToken, expires_at);
  }

  /**
   * Perform federated sign in with exponential backoff retry logic
   * 
   * @param {string} sessionToken - The session token from STS
   * @param {number} expires_at - Expiration timestamp
   * @param {number} retries - Number of retries remaining (default: MAX_RETRIES)
   * @param {number} backoff - Base backoff delay in milliseconds (default: 1000ms)
   * 
   * Retry delays follow exponential backoff with jitter:
   * - Attempt 1: 1000ms + 0-500ms jitter
   * - Attempt 2: 2000ms + 0-500ms jitter  
   * - Attempt 3: 4000ms + 0-500ms jitter
   */
  async performFederatedSignIn(sessionToken, expires_at, retries = MAX_RETRIES, backoff = 1000){
    // Safeguard against excessive retry values
    if (retries > MAX_RETRIES) retries = MAX_RETRIES;
    
    const user = this.getUserDataFromURL();
    
    try {
      await Auth.federatedSignIn('developer', { token: sessionToken, expires_at }, user);
      const resp = await Auth.currentAuthenticatedUser();
      this.cacheData(resp);
      return resp;
    } catch (err) {
      console.error("Error in performFederatedSignIn", {
        message: err.message,
        code: err.code,
        name: err.name,
        statusCode: err.statusCode,
        retries,
        stack: err.stack
      });
      
      if (retries > 0) {
        // Calculate exponential backoff with jitter: base * 2^(attempt) + random jitter up to 500ms
        const attempt = MAX_RETRIES - retries; // 0-based attempt number
        const exponentialBackoff = backoff * Math.pow(2, attempt);
        
        const jitter = Math.random() * 500; // Add up to 500ms random jitter
        const delay = exponentialBackoff + jitter;
        
        console.log(`Retrying performFederatedSignIn in ${Math.round(delay)}ms (attempt ${attempt + 1}/${MAX_RETRIES})`);
        
        // Wait for the calculated delay
        await new Promise(resolve => setTimeout(resolve, delay));
        
        // Retry the performFederatedSignIn call
        return this.performFederatedSignIn(sessionToken, expires_at, retries - 1, backoff);
      } else {
        console.error("performFederatedSignIn failed after all retries", err);
        // hadSessionArgument is expected to be false: assumeRole passes `data.Credentials.sessionToken`,
        // which is undefined (the SDK field is SessionToken), and the app relies on that. The key
        // avoids the word "token", which Sentry's scrubber replaces with [Filtered].
        logSentryException(new Error('Federated sign-in failure'), 'Error in AppContainer.performFederatedSignIn', {
          // originalErrorType, not originalName: sanitizeDataForSentry deletes any key containing "name".
          originalError: err?.message, originalCode: err?.code, originalErrorType: err?.name, originalStack: err?.stack,
          hadSessionArgument: Boolean(sessionToken),
        }, { tags: { awsErrorCode: err?.code || 'none' } });
        throw err;
      }
    }
  }

  /**
   * Assume role with exponential backoff retry logic
   * 
   * @param {number} retries - Number of retries remaining (default: MAX_RETRIES)
   * @param {number} backoff - Base backoff delay in milliseconds (default: 1000ms)
   * 
   * Retry delays follow exponential backoff with jitter:
   * - Attempt 1: 1000ms + 0-500ms jitter
   * - Attempt 2: 2000ms + 0-500ms jitter  
   * - Attempt 3: 4000ms + 0-500ms jitter
   */
  async assumeRole(retries = MAX_RETRIES, backoff = 1000){
    // Safeguard against excessive retry values
    if (retries > MAX_RETRIES) retries = MAX_RETRIES;
    const params = {
      DurationSeconds: STS_DURATION_SECONDS,
      RoleArn: process.env.ASSUME_ROLE_ARN,
      WebIdentityToken: token,
      RoleSessionName: "student-sso" // shows up in CloudTrail, so make it searchable
    };

    try {
      // Convert callback-based STS call to Promise
      const data = await new Promise((resolve, reject) => {
        sts.assumeRoleWithWebIdentity(params, (err, data) => {
          if (err) {
            reject(err);
          } else {
            resolve(data);
          }
        });
      });

      // Success - update AWS config and proceed with login
      AWS.config.update({
        accessKeyId: data.Credentials.AccessKeyId,
        secretAccessKey: data.Credentials.SecretAccessKey,
        sessionToken: data.Credentials.SessionToken
      });
      this.startSessionCheck();
      // GT-102: every later Sentry event carries the STS session's issue and expiry times, so the
      // credential age at any failure is event time minus issuedAt. Names avoid the words
      // "credentials" and "token", which Sentry's scrubber replaces with [Filtered].
      // issuedAt is the device clock; expiration is STS's clock. clockSkewSeconds is the difference
      // the two imply (positive when the device clock is behind), so a skewed Chromebook is visible.
      const expiration = data.Credentials.Expiration ? new Date(data.Credentials.Expiration).getTime() : null;
      Sentry.setContext('stsSession', {
        issuedAt: new Date().toISOString(),
        expiration: expiration ? new Date(expiration).toISOString() : null,
        durationSeconds: STS_DURATION_SECONDS,
        clockSkewSeconds: expiration ? Math.round((expiration - Date.now()) / 1000 - STS_DURATION_SECONDS) : null,
      });
      // Deliberately `sessionToken` (undefined): Amplify then federates with no login and the app
      // signs with the STS credentials above. Passing the real SessionToken breaks every SSO login.
      // A sign-in that fails after its own retries used to be an unhandled rejection that left the
      // student on Loading; end the session instead.
      this.federatedLogIn(data.Credentials.sessionToken)
        .catch(() => this.endExpiredSession('federated_sign_in_failed'));

    } catch (err) {
      console.error("Error in assumeRole", {
        message: err.message,
        code: err.code,
        statusCode: err.statusCode,
        retries,
        stack: err.stack
      });
      
      if (retries > 0) {
        // Calculate exponential backoff with jitter: base * 2^(attempt) + random jitter up to 500ms
        const attempt = MAX_RETRIES - retries; // 0-based attempt number
        const exponentialBackoff = backoff * Math.pow(2, attempt);
        const jitter = Math.random() * 500; // Add up to 500ms random jitter
        const delay = exponentialBackoff + jitter;
        
        console.log(`Retrying assumeRole in ${Math.round(delay)}ms (attempt ${attempt + 1}/${MAX_RETRIES})`);
        
        // Wait for the calculated delay
        await new Promise(resolve => setTimeout(resolve, delay));
        
        // Retry the assumeRole call
        return this.assumeRole(retries - 1, backoff);
      } else {
        console.error("assumeRole failed after all retries", err);
        // Usually a reloaded or restored tab with a stale launch token. Nothing awaits this call, so
        // a throw was only an unhandled rejection (auto-captured by Sentry as AMIRA-WEB-APP-4ZNQ) and
        // the student stayed on Loading forever. Keep it visible to Sentry, then end the session.
        logSentryException(new Error('STS assume role failure'), 'Error in AppContainer.assumeRole', {
          originalError: err?.message, originalCode: err?.code, originalStack: err?.stack,
        }, { tags: { awsErrorCode: err?.code || 'none' } });
        this.endExpiredSession('assume_role_failed');
      }
    }
  }

  cacheData(data){
    data.locale = urlParams.get('locale');
    data.region = urlParams.get('region');
    saveUserData(data)
    .then(resp =>{
      //Tell other Amira windows to log out.
      message_broadcast('logout');
      //Start listening for other instances of Amira (do it here before we grab the mic)
      window.addEventListener('storage', (evt) => {
        message_receive(evt);
      });
      const initAudioIfNeeded = () => {
        if (this.state.preChecksDone) {
          initAudio();
        } else {
          return Promise.resolve();
        }
      }

      initAudioIfNeeded()
      .then(()=>{
        this.setState({
          userData: resp,
        });
      })
    })
    .catch((err) =>{
      console.log("ERROR IN SETTING LOGGED IN USER!", JSON.stringify(err))
      endUserSession();
    });
  }

  setPreChecksDone(preChecksDone) {
    this.setState({ preChecksDone });
  }

  render() {
    // The user must be Authenticated to access the App
    if(SIGNEDIN_MODE[this.props.authState] && this.state.loading){
      return <Loading/>
    }
    if(!SIGNEDIN_MODE[this.props.authState] || !this.state.userData){
      return null;
    }

    const { userType } = this.state.userData;
    const isTeacherMode = this.props.location.pathname.includes('teacher') || this.props.location.pathname.includes('admin')

    let appContainerClasses = cx({
      'appContainer': true,
      'teacherMode' : isTeacherMode,
      'studentMode' : !isTeacherMode
    });

    let appConfig = config[userType];

    //TODO: We shouldn't need to condition routes like this (a lot of routes currently depend on App.js state/methods/props)
    return (
      <div className={appContainerClasses}>
        <Route path="/(teacher|reports|admin)/" render={(props) => <GlobalNav {...props}/>} />
        <Orientation orientation={this.state.orientation} userRole={this.state.userData && this.state.userData.userType}/>
        <App
          {...this.props}
          config={appConfig}
          selectStories={urlParams.get('select')}
          forceStory={urlParams.get('storyId')}
          forceCalibration={urlParams.get('calibration')}
          forceDialogFTUE={urlParams.get('forceDialogFTUE')}
          forceTutor={urlParams.get('forceTutor')}
          forceGooglePM={urlParams.get('googlePM')}
          noAvatar={urlParams.get('noAvatar')}
          forceIntervention={urlParams.get('interventionType')}
          interventionBias={urlParams.get('interventionBias')}
          skipIntros={urlParams.get('skipIntros')}
          isSpanishPilot={isSpanishPilot}
          setPreChecksDone={this.setPreChecksDone}
          forceInterventionSelectionGrade={urlParams.get('interventionSelectionGrade')}
          forceInterventionSelectionWCPM={urlParams.get('interventionSelectionWCPM')}
          forceInterventionSelectionARM={urlParams.get('interventionSelectionARM')}
          region={urlParams.get('region')}
          assignmentId={urlParams.get('assignmentId')}
          assignmentType={urlParams.get('assignmentType')}
          manifestItemId={urlParams.get('manifestId')}
          returnUrl={urlParams.get('inboxReturnUrl') ? decodeURIComponent(urlParams.get('inboxReturnUrl')) : undefined}
          fromStudentInbox={urlParams.get('inboxReturnUrl') ? true : false}
          tutorLocale={urlParams.get('tutorLocale')}
          amiraIsipPilotGuid={urlParams.get('amiraIsipPilotGuid')}
          spanishIsipCalibrationGuid={urlParams.get('amiraSpanishIsipPilotGuid')}
          isSpanishPilot={urlParams.get('isSpanishPilot') === 'true'}
          goToProgressPage={urlParams.get('goToProgress')}
          goToTutorPicker={urlParams.get('goToTutorPicker')}
          skillId={urlParams.get('skillId')}
          onCampus={urlParams.get('isAuthorizedIp') === 'true' || urlParams.get('isAuthorizedIp') === '1'}
          teacherMicTest={urlParams.get('teacherMicTest')}
          forceBrand={urlParams.get('forceBrand')}
          webglError={this.props.webglProps.webglError}
        />
        <DebugMenu />
      </div>
    );
  }
}

function Footer({ authState }) {
  const [footerText, setFooterText] = useState('');
  const [copyrightText, setCopyrightText] = useState('');

  useEffect(() => {
    setFooterText(GetBrandedFooter());
    setCopyrightText(GetBrandedCopyright());
  }, [])

  if(SIGNEDIN_MODE[authState]){
    return null;
  }
  return (
    <div className="footer">
      <div className="footerWrapper">
        <span className="footerContent">{footerText}</span>
        <span className="footerContent">{copyrightText}</span>
      </div>
    </div>
  );
}

export default class AppWithAuth extends React.Component {
  render(){

    // pull initial authState from URL params. Defaults to signIn
    const isSSO = urlParams.get('token') ? true : false;
    let authState = isSSO ? 'signedIn' : AUTH_MODES[urlParams.get('authState')] || AUTH_MODES.signin;
    return (
      <Provider store={this.props.store}>
        <Router basename={GITHUB_PAGES_ROOT_PATH}>
          <AmiraAuthenticator
            includeGreetings={false}
            hideDefault={true}
            authState={authState}
            federated={isSSO}
            hide={isSSO ? ["AmiraSignIn"] : []}
          >
            <AmiraSignIn/>
            <AppContainer webglProps={this.props.webglProps} location={location}/>
            <Footer />
          </AmiraAuthenticator>
        </Router>
      </Provider>
    );
  }
}
